import {
  ArrowDown,
  ArrowUp,
  Filter,
  SortDown,
  SortUp,
} from '@randstad-lean-mobile-factory/react-assets/dist/icons';
import { Dispatch, SetStateAction, useRef, useState } from 'react';

import { Button } from '../../Button/Button';
import { MenuItem } from '../../Menus';
import { Menu, MenuActions } from '../../Menus';
import { WithLightTitle } from '../../WithLightTitle';

import styles from './ColumnFilterPopup.module.scss';
import { ColumnFilterPopupProps, isFilterHandled } from './ColumnFilterPopup.types';
import { RangeFilter } from './RangeFilter';
import { SelectFilter } from './SelectFilter';

export const ColumnFilterPopup = <TData, TValue>({
  column,
  className,
}: ColumnFilterPopupProps<TData, TValue>) => {
  const [sortValue, setSortValue] = useState(column.getIsSorted());
  const [filterValue, setFilterValue] = useState(column.getFilterValue());

  const ref = useRef<MenuActions>(null);

  if (!isFilterHandled(column)) {
    return (
      <>
        <Button
          variant="ghost"
          size="small"
          selected={column.getIsSorted() === 'asc'}
          onClick={() => {
            if (column.getIsSorted() === 'asc') column.clearSorting();
            else column.toggleSorting(false);
          }}
        >
          <ArrowUp />
        </Button>
        <Button
          variant="ghost"
          size="small"
          selected={column.getIsSorted() === 'desc'}
          onClick={() => {
            if (column.getIsSorted() === 'desc') column.clearSorting();
            else column.toggleSorting(true);
          }}
        >
          <ArrowDown />
        </Button>
      </>
    );
  }

  return (
    <Menu
      trigger={
        <Button
          variant="ghost"
          size="small"
          selected={Boolean(
            column.getIsSorted() || (column.getIsFiltered() && isFilterHandled(column))
          )}
          className={className}
        >
          <Filter />
        </Button>
      }
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      ref={ref}
      onOpen={() => {
        setSortValue(column.getIsSorted());
        setFilterValue(column.getFilterValue());
      }}
      className={styles.menu}
    >
      {column.getCanSort() && (
        <>
          <WithLightTitle
            title={column?.columnDef?.meta?.columnSortingLabel ?? 'par ordre'}
            titleClassName={styles.label}
            className={styles.sort}
          >
            <MenuItem
              keepOpenOnClick
              selected={sortValue === 'asc'}
              onClick={() => {
                if (sortValue === 'asc') setSortValue(false);
                else setSortValue('asc');
              }}
            >
              <SortUp />
              croissant
            </MenuItem>
            <MenuItem
              keepOpenOnClick
              selected={sortValue === 'desc'}
              onClick={() => {
                if (sortValue === 'desc') setSortValue(false);
                else setSortValue('desc');
              }}
            >
              <SortDown />
              décroissant
            </MenuItem>
          </WithLightTitle>
          <div className={styles.hr} />
        </>
      )}
      <div className={styles.filter}>
        {column.columnDef.meta?.customFilter ? (
          <column.columnDef.meta.customFilter
            column={column}
            filterValue={filterValue}
            setFilterValue={setFilterValue}
          />
        ) : column.columnDef.filterFn === 'arrIncludes' ? (
          <SelectFilter
            column={column}
            filterValue={filterValue as (string | number)[] | undefined}
            setFilterValue={
              setFilterValue as Dispatch<SetStateAction<(string | number)[] | undefined>>
            }
            emptyItems="aucun élément correspondant"
          />
        ) : column.columnDef.filterFn === 'inNumberRange' ? (
          <RangeFilter
            column={column}
            filterValue={filterValue as [number, number] | undefined}
            setFilterValue={
              setFilterValue as Dispatch<SetStateAction<[number, number] | undefined>>
            }
          />
        ) : null}
      </div>
      {column.getCanSort() && <div className={styles.hr} />}
      <div className={styles.footer}>
        <Button variant="tertiary" size="small" onClick={() => ref.current?.close()}>
          annuler
        </Button>
        <Button
          size="small"
          onClick={() => {
            column.setFilterValue(filterValue);
            if (sortValue === 'asc') column.toggleSorting(false);
            else if (sortValue === 'desc') column.toggleSorting(true);
            else column.clearSorting();
            ref.current?.close();
          }}
        >
          valider
        </Button>
      </div>
    </Menu>
  );
};
